import {useQuery} from '@tanstack/react-query';
import React from 'react';

import {api} from 'app/modules/Remote';
import {ApiOrganization} from 'app/modules/Remote/Organization';

import {ApiAgentWorkflow} from './types';

const WorkflowsContext = React.createContext<
  | {
      workflows: ApiAgentWorkflow[];
      isFetchingWorkflows: boolean;
      refetch: () => void;
    }
  | undefined
>(undefined);

export const WorkflowsProvider: React.FC<
  React.PropsWithChildren<{organization: ApiOrganization}>
> = ({organization, children}) => {
  const organizationId = organization.id;
  const {
    data: workflows,
    isFetching: isFetchingWorkflows,
    refetch,
  } = useAgentWorkflowsQuery(organizationId);

  return (
    <WorkflowsContext.Provider
      value={{
        workflows: workflows || [],
        isFetchingWorkflows,
        refetch,
      }}
    >
      {children}
    </WorkflowsContext.Provider>
  );
};

export const useWorkflowsContext = () => {
  const context = React.useContext(WorkflowsContext);
  if (!context) {
    throw new Error('useWorkflowsContext must be used within a WorkflowsProvider');
  }
  return context;
};

export const useAgentWorkflowsQuery = (orgId: string) => {
  return useQuery({
    queryKey: ['agent-workflows', orgId],
    queryFn: async () => {
      const workflows = (await api.organizations.workflows(orgId)).get('data');
      return workflows.toJS() as ApiAgentWorkflow[];
    },
    enabled: !!orgId,
  });
};
